import { useMemo } from "react";

import { Card, CardContent, Grid, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Gauge, gaugeClasses } from "@mui/x-charts";

import { EmailType } from "@interfaces/campaign.type";

import theme from "@theme/index";

interface IGaugeMetricsProperties {
  emails: EmailType[];
  hasBoxShadow?: boolean;
}

interface IGaugeMetrics {
  bounced: number;
  bouncedRatePercentage: string;
  clicked: number;
  clickedRatePercentage: string;
  opened: number;
  openRatePercentage: string;
  replied: number;
  repliedRatePercentage: string;
}

export const handleGaugeValue = (emails: EmailType[]) => {
  return emails.reduce(
    (accumulator, email) => {
      if (email.opened > 0) {
        accumulator.opened++;
        accumulator.openRatePercentage = Math.round((accumulator.opened / emails.length) * 100).toString();
      }
      if (email.linkClicked > 0) {
        accumulator.clicked++;
        accumulator.clickedRatePercentage = Math.round((accumulator.clicked / emails.length) * 100).toString();
      }
      if (email.bounced) {
        accumulator.bounced++;
        accumulator.bouncedRatePercentage = Math.round((accumulator.bounced / emails.length) * 100).toString();
      }
      if (email.replyReceived > 0) {
        accumulator.replied++;
        accumulator.repliedRatePercentage = Math.round((accumulator.replied / emails.length) * 100).toString();
      }
      return accumulator;
    },
    {
      bounced: 0,
      bouncedRatePercentage: "",
      clicked: 0,
      clickedRatePercentage: "",
      opened: 0,
      openRatePercentage: "",
      replied: 0,
      repliedRatePercentage: "",
    } as IGaugeMetrics,
  );
};

export default function GaugeMetrics({ emails, hasBoxShadow = true }: IGaugeMetricsProperties) {
  const itemsToRender = useMemo(() => {
    const { bouncedRatePercentage, clickedRatePercentage, openRatePercentage, repliedRatePercentage } =
      handleGaugeValue(emails);
    return [
      {
        title: "Open Rate",
        tooltip: "Percentage of contacts who opened your email.",
        value: openRatePercentage,
      },
      {
        title: "Click Rate",
        tooltip: "Percentage of contacts who clicked a link in your email.",
        value: clickedRatePercentage,
      },
      {
        title: "Bounce Rate",
        tooltip: "Percentage of emails that bounced back.",
        value: bouncedRatePercentage,
      },
      {
        title: "Response Rate",
        tooltip: "Percentage of contacts who replied to your email.",
        value: repliedRatePercentage,
      },
    ];
  }, [emails]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card
          sx={{
            backgroundColor: theme.palette.grey[50],
            ...(hasBoxShadow
              ? {}
              : {
                  border: `1px solid ${theme.palette.grey[300]}`,
                  boxShadow: "none",
                }),
            color: theme.palette.primary.contrastText,
            display: "flex",
            flexDirection: "column",
            minHeight: "156px",
          }}
        >
          <Grid container spacing={2}>
            {itemsToRender.map((item, index) => (
              <Grid item key={index} lg={3} md={6} xs={12}>
                <Tooltip placement="bottom" title={item.tooltip}>
                  <CardContent
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      flexDirection: "column",
                      height: "156px",
                      justifyContent: "center",
                    }}
                  >
                    <Typography fontWeight={700} variant="body2">
                      {item.title}
                    </Typography>
                    <Gauge
                      endAngle={90}
                      startAngle={-90}
                      sx={{
                        [`& .${gaugeClasses.referenceArc}`]: {
                          fill: theme.palette.grey[400],
                        },
                        [`& .${gaugeClasses.valueArc}`]: {
                          fill: theme.palette.primary.main,
                        },
                        [`& .${gaugeClasses.valueText}`]: {
                          color: theme.palette.primary.main,
                          fontSize: "24px",
                          fontWeight: 600,
                          transform: "translateY(-12px)",
                        },
                      }}
                      text={item.value ? `${item.value}%` : "-"}
                      value={Number(item.value)}
                    />
                  </CardContent>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
}
